import { Component } from '@theme/component';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/** Animates server-rendered shipping progress without changing cart totals or requests. */
class HoverMistCartProgress extends Component {
  static observedAttributes = ['data-progress'];

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  #drawer = null;
  /** @type {HTMLElement | null} */
  #fill = null;
  /** @type {Animation | null} */
  #animation = null;
  #value = 0;
  #frame = 0;
  #restart = false;
  #motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();
    this.#drawer = this.closest('theme-drawer');
    this.#drawer?.addEventListener(DrawerOpenEvent.eventName, this.#onOpen);
    this.#drawer?.addEventListener(DrawerCloseEvent.eventName, this.#stop);
    this.#motionQuery.addEventListener('change', this.#onMotionChange);
    this.#schedule();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#schedule();
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#schedule();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#drawer?.removeEventListener(DrawerOpenEvent.eventName, this.#onOpen);
    this.#drawer?.removeEventListener(DrawerCloseEvent.eventName, this.#stop);
    this.#motionQuery.removeEventListener('change', this.#onMotionChange);
    this.#stop();
    this.#drawer = null;
    this.#fill = null;
  }

  #onOpen = () => {
    this.#restart = true;
    this.#schedule();
  };

  #onMotionChange = () => {
    this.#stop();
    this.#schedule();
  };

  #stop = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    this.#restart = false;
    this.#animation?.cancel();
    this.#animation = null;
  };

  #schedule() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      if (!this.isConnected) return;
      this.#animate();
    });
  }

  #animate() {
    const fill = this.querySelector('.hm-cart-progress__bar > span');
    if (!(fill instanceof HTMLElement)) return;

    const progress = Number(this.dataset.progress);
    const value = Number.isFinite(progress) ? Math.min(100, Math.max(0, progress)) / 100 : 0;
    const restart = this.#restart;
    this.#restart = false;
    let from = restart ? 0 : this.#value;

    // Keep rapid updates continuous even if the previous animation has not finished.
    if (!restart && this.#animation && this.#fill === fill) {
      from = new DOMMatrixReadOnly(getComputedStyle(fill).transform).a;
    }

    this.#animation?.cancel();
    this.#animation = null;
    this.#fill = fill;
    this.#value = value;

    if (!this.#drawer?.isOpen || from === value) return;

    const reducedMotion = this.#motionQuery.matches && this.dataset.motion !== 'always';

    const animation = fill.animate(
      reducedMotion
        ? [{ opacity: 0.25 }, { opacity: 1 }]
        : [{ transform: `scaleX(${from})` }, { transform: `scaleX(${value})` }],
      {
        duration: reducedMotion ? 600 : restart ? 1000 : 650,
        easing: getComputedStyle(this).getPropertyValue('--ease-out-cubic').trim() || 'cubic-bezier(0.33, 1, 0.68, 1)',
      }
    );
    this.#animation = animation;
    animation.onfinish = () => {
      if (this.#animation === animation) this.#animation = null;
    };
  }
}

if (!customElements.get('hovermist-cart-progress')) {
  customElements.define('hovermist-cart-progress', HoverMistCartProgress);
}
